<template>
  <div class="product-items" v-if="items && items.length" v-resize="handleResize">
    <div class="product-items__inner" ref="productItemsInner">
      <product-item
        v-for="(item, idx) in items"
        :id="'p-' + item.id"
        :item="item"
        :index="idx + 1"
        :key="item.id"
        :class="{ 'product-item_active': idx + 1 == activeItem }"
        :style="`order:${idx + 1}`"
        @toggle="toggleAccordion"
      />
      <product-accordion
        v-if="productAccordionData"
        :content="productAccordionData"
        :style="`order:${productAccordionOrder}`"
        @close="closeAccordion"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';

  // Type definitions for product items
  interface ProductItem {
    id: string | number;
    external?: {
      title?: string;
      [key: string]: any;
    };
    internal?: {
      title?: string;
      [key: string]: any;
    };
    publishedTo?: number;
    resources?: any[];
    [key: string]: any;
  }

  interface ToggleData {
    index: number;
    item: any; // Use more flexible type for compatibility
  }

  interface NavigationHeight {
    onScrollDown: number;
    onScrollUp: number;
  }

  const props = defineProps<{
    items?: ProductItem[];
  }>();

  const mainStore = useMainStore();
  const baseUrlMedia = computed(() => mainStore.baseUrlMedia);
  const tabChangeState = computed<boolean>(() => Boolean(mainStore.tabChangeState));

  const productItemsNumberInRow = ref(0);
  const productAccordionOrder = ref(0);
  const productAccordionData = ref<{ item: ProductItem } | null>(null);
  const activeItem = ref(0);
  const initialHashApplied = ref(false);
  const navigationHeight = ref<NavigationHeight>({
    onScrollDown: 0,
    onScrollUp: 0
  });

  const productItemsInner = ref<HTMLElement | null>(null);

  function getNavigationHeight(): void {
    const breadcrumbs = document.getElementById('breadcrumbs');
    const navigationInner = document.getElementById('navigation__inner');

    if (navigationInner) {
      let breadcrumbsHeight = breadcrumbs ? breadcrumbs.clientHeight : 0;
      let navigationHeightVal = navigationInner.clientHeight;
      navigationHeight.value = {
        onScrollDown: navigationHeightVal - breadcrumbsHeight,
        onScrollUp: navigationHeightVal
      };
    }
  }

  function getProductItemsNumberInRow(): void {
    const container = productItemsInner.value;
    if (!container) return;
    const itemsHolderWidth = container.getBoundingClientRect().width;
    const firstChild = container.children && container.children.length > 0 ? container.children[0] : null;
    if (!firstChild) return;
    const itemWidth = firstChild.getBoundingClientRect().width || 1;
    const perRow = Math.floor(itemsHolderWidth / itemWidth) || 1;
    productItemsNumberInRow.value = Math.max(1, perRow);
  }

  function getProductAccordionOrder(index: number): void {
    if (index == 0) index++;
    if (!productItemsNumberInRow.value) getProductItemsNumberInRow();
    const perRow = productItemsNumberInRow.value || 1;
    let rowNumber = Math.ceil(index / perRow);
    productAccordionOrder.value = rowNumber * perRow;
  }

  function toggleAccordion(val: ToggleData): void {
    // Use history.replaceState to avoid automatic scroll behavior
    const hashId = '#p-' + val.item.id;
    const newUrl = window.location.pathname + window.location.search + hashId;
    history.replaceState(null, '', newUrl);

    if (val.index === activeItem.value) {
      history.replaceState(null, '', ' ');
      closeAccordion();
      return;
    }
    productAccordionData.value = null;
    activeItem.value = val.index;
    getProductAccordionOrder(activeItem.value);
    scrollToAccordion(hashId);
    setTimeout(() => {
      // ProductAccordion expects "content" with shape { item: {...}, items?: [...] }
      productAccordionData.value = { item: val.item };
    }, 0);
  }

  function openItemByHash(hashId: string): boolean {
    const items = props.items ?? [];
    const itemIndex = items.findIndex((entry: ProductItem) => '#p-' + entry.id === hashId);
    if (itemIndex < 0) return false;

    const item = items[itemIndex];
    if (!item) return false;

    if (activeItem.value === itemIndex + 1 && productAccordionData.value?.item?.id === item.id) {
      return true;
    }

    activeItem.value = itemIndex + 1;
    getProductAccordionOrder(activeItem.value);
    scrollToAccordion(hashId);

    setTimeout(() => {
      // ProductAccordion expects "content" with shape { item: {...}, items?: [...] }
      productAccordionData.value = { item };
    }, 0);

    return true;
  }

  function applyInitialHashSelection(): void {
    if (initialHashApplied.value) return;
    if (typeof window === 'undefined' || !props.items || !props.items.length) return;

    const hashId = window.location.hash || '';
    if (!hashId) return;

    if (openItemByHash(hashId)) {
      initialHashApplied.value = true;
    }
  }

  function closeAccordion(): void {
    activeItem.value = 0;
    productAccordionData.value = null;
  }

  function scrollToAccordion(hashId: string): void {
    const container = productItemsInner.value;
    if (!container) return;

    // Check if scroll is locked (modal is open) or scroll operation is in progress - if so, skip the scroll operation
    if (mainStore.scrollLockState.isLocked || mainStore.scrollLockState.isScrollOperationInProgress) {
      return;
    }

    const children = container.children;
    const itemLeftPos = 0;
    const child = Array.from(children).find((el) => '#' + (el as HTMLElement).id === hashId) as HTMLElement;
    if (!child) return;
    let itemHeight = child.getBoundingClientRect().height;
    let screen = window.innerWidth > 767 ? 'desktop' : 'mobile';
    if (screen == 'mobile') {
      setTimeout(() => {
        // Double-check scroll lock state and operation status before performing scroll
        if (mainStore.scrollLockState.isLocked || mainStore.scrollLockState.isScrollOperationInProgress) {
          return;
        }

        let productItemBarHeight = 48;
        let headerEl = document.getElementById('header');
        let headerHeight = headerEl ? headerEl.clientHeight : 0;
        let lastScrollPosition = window.scrollY || window.pageYOffset;
        let itemTopPos = child.getBoundingClientRect().top;
        let scrollDirection = itemTopPos + headerHeight >= navigationHeight.value.onScrollDown ? 'down' : 'up';
        itemTopPos += lastScrollPosition;
        if (scrollDirection == 'down') {
          window.scrollTo({
            top: itemTopPos + itemHeight - navigationHeight.value.onScrollDown,
            left: itemLeftPos,
            behavior: 'smooth'
          });
        } else {
          window.scrollTo({
            top: itemTopPos + itemHeight - (navigationHeight.value.onScrollUp + headerHeight + productItemBarHeight),
            left: itemLeftPos,
            behavior: 'smooth'
          });
        }
      }, 300);
    } else {
      setTimeout(() => {
        // Double-check scroll lock state and operation status before performing scroll
        if (mainStore.scrollLockState.isLocked || mainStore.scrollLockState.isScrollOperationInProgress) {
          return;
        }

        let lastScrollPosition = window.scrollY || window.pageYOffset;
        let itemTopPos = child.getBoundingClientRect().top;
        let scrollDirection = itemTopPos >= navigationHeight.value.onScrollDown - 2 ? 'down' : 'up';
        itemTopPos += lastScrollPosition;
        if (scrollDirection == 'down') {
          window.scrollTo({
            top: itemTopPos - navigationHeight.value.onScrollDown,
            left: itemLeftPos,
            behavior: 'smooth'
          });
        } else {
          window.scrollTo({
            top: itemTopPos - navigationHeight.value.onScrollUp,
            left: itemLeftPos,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  }

  function handleResize(): void {
    getProductItemsNumberInRow();
    getProductAccordionOrder(activeItem.value);
    getNavigationHeight();
  }

  onMounted(() => {
    getProductItemsNumberInRow();
    getNavigationHeight();
    applyInitialHashSelection();
  });

  watch(
    () => props.items?.length,
    () => {
      applyInitialHashSelection();
    }
  );

  // Reset accordion only when tabs change (intentional UX), not on every update
  watch(tabChangeState, (val: boolean) => {
    if (val) {
      activeItem.value = 0;
      productAccordionData.value = null;
      productAccordionOrder.value = 0;
      getProductItemsNumberInRow();
      if (typeof mainStore.setTabChangeState === 'function') {
        mainStore.setTabChangeState(false);
      }
    }
  });
</script>
